import React from 'react';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Building2, Package, MessageSquare, Star } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * ReviewsProductsTable - Tableau des avis Retailer
 * 
 * Composant tableau responsive pour afficher les avis laissés par le Retailer avec:
 * - Visuel produit
 * - Désignation produit
 * - Distributeur
 * - Avis (texte)
 * - Note (étoiles)
 * 
 * Props:
 * @param {Array} reviews - Liste des avis/commentaires (avec product enrichi)
 * @param {boolean} loading - État de chargement
 */
const ReviewsProductsTable = ({ 
  reviews = [], 
  loading = false
}) => {

  // Obtenir l'image du produit
  const getProductImage = (review) => {
    return review.product_image || null;
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Composant étoiles de notation
  const StarRating = ({ rating, size = 'sm' }) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const sizeClass = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
    
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`${sizeClass} ${i < fullStars ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
        />
      );
    }
    
    return <div className="flex items-center gap-0.5">{stars}</div>;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Desktop/Tablet Table View */}
      <div className="hidden sm:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-[80px]">Visuel</TableHead>
              <TableHead className="min-w-[180px]">Désignation</TableHead>
              <TableHead className="min-w-[130px]">Distributeur</TableHead>
              <TableHead className="min-w-[250px]">Avis</TableHead>
              <TableHead className="w-[120px] text-center">Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Vous n'avez pas encore laissé d'avis</p>
                  <p className="text-sm mt-2">Partagez votre expérience sur les produits</p>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((review) => {
                const imageUrl = getProductImage(review);
                
                return (
                  <TableRow 
                    key={review.id} 
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`review-row-${review.id}`}
                  >
                    {/* Visuel */}
                    <TableCell className="p-2">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={review.product_intitule}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-full h-full items-center justify-center ${imageUrl ? 'hidden' : 'flex'}`}
                        >
                          <Package className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>
                    </TableCell>
                    
                    {/* Désignation */}
                    <TableCell>
                      <Link 
                        to={`/dashboard/produit/${review.product_id}`}
                        className="group flex flex-col gap-1"
                      >
                        <span className="font-medium text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
                          {review.product_intitule || 'Produit'}
                        </span>
                        {review.product_editeur && (
                          <span className="text-xs text-muted-foreground">
                            {review.product_editeur}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDate(review.created_at)}
                        </span>
                      </Link>
                    </TableCell>
                    
                    {/* Distributeur */}
                    <TableCell>
                      {review.partner_name ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm">{review.partner_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    
                    {/* Avis */}
                    <TableCell>
                      <div className="space-y-1">
                        {review.title && (
                          <p className="font-medium text-sm text-gray-900">
                            {review.title}
                          </p>
                        )}
                        <p className="text-sm text-gray-700 line-clamp-3">
                          {review.commentaire || <span className="italic text-muted-foreground">Pas de commentaire</span>}
                        </p>
                      </div>
                    </TableCell>
                    
                    {/* Note */}
                    <TableCell className="text-center">
                      {review.rating ? (
                        <div className="flex flex-col items-center gap-1">
                          <StarRating rating={review.rating} />
                          <span className="text-xs text-muted-foreground">
                            {review.rating}/5
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden divide-y">
        {reviews.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Vous n'avez pas encore laissé d'avis</p>
            <p className="text-sm mt-2">Partagez votre expérience sur les produits</p>
          </div>
        ) : (
          reviews.map((review) => {
            const imageUrl = getProductImage(review);
            
            return (
              <div 
                key={review.id}
                className="p-4 hover:bg-gray-50 transition-colors"
                data-testid={`review-card-mobile-${review.id}`}
              >
                <div className="flex gap-4">
                  {/* Visuel */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={review.product_intitule}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Package className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <Link 
                        to={`/dashboard/produit/${review.product_id}`}
                        className="font-medium text-gray-900 hover:text-primary transition-colors line-clamp-2 flex-1"
                      >
                        {review.product_intitule || 'Produit'}
                      </Link>
                    </div>
                    
                    {/* Distributeur */}
                    {review.partner_name && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Building2 className="h-3 w-3 text-blue-500" />
                        {review.partner_name}
                      </div>
                    )}
                    
                    {/* Note */}
                    {review.rating && (
                      <div className="flex items-center gap-2 mt-2">
                        <StarRating rating={review.rating} size="sm" />
                        <span className="text-xs text-muted-foreground">
                          {review.rating}/5
                        </span>
                      </div>
                    )}
                    
                    {/* Date */}
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(review.created_at)}
                    </p>
                  </div>
                </div>
                
                {/* Avis - Mobile */}
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      {review.title && (
                        <p className="font-medium text-sm text-gray-900 mb-1">
                          {review.title}
                        </p>
                      )}
                      <p className="text-sm text-gray-700">
                        {review.commentaire || <span className="italic text-muted-foreground">Pas de commentaire</span>}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ReviewsProductsTable;
